/*----------------------------------
- TLD INSIGHT ANSWER
----------------------------------*/

/**
 * @docs docs/features/tld-intelligence
 * @adr  ADR-0054
 * @rule One mapping of Product's insight status for `/tld/<slug>` and its full mirror
 *       `/tld/<slug>.md` (D28): `blocked` 301 to the hub (the hub's mirror for the Markdown
 *       twin), `gone` 410, `not_public` 404, `unavailable` 503 with `Retry-After`.
 *
 * Pure, so tests/helpers/tld-pages-contract.test.cjs asserts every status by behaviour.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import type { PublicTldInsight, PublicTldInsightResult } from '@uniquedomains/components/contracts/tlds/public';
import { TLD_HUB_PAGE_MIRROR_PATH, TLD_HUB_PATH } from '@uniquedomains/core/domains/tlds';

import { PUBLIC_UNAVAILABLE_RETRY_AFTER_SECONDS } from '@/client/pages/landing/lib/publicUnavailable';

/*----------------------------------
- TYPES
----------------------------------*/

/** The page renders HTML error pages; its Markdown twin answers the mirrors' plain text. */
export type TldInsightSurface = 'page' | 'markdown';

export type TldInsightAnswer =
    | { kind: 'render'; insight: PublicTldInsight }
    | { kind: 'redirect'; path: string }
    | { kind: 'error'; status: 404 }
    | { kind: 'error'; status: 410 }
    /** No nightly rollup yet for this TLD: retryable, and never cached by the warmup. */
    | { kind: 'error'; status: 503; retryAfter: string };

/*----------------------------------
- PUBLIC API
----------------------------------*/

export const resolveTldInsightAnswer = (result: PublicTldInsightResult, surface: TldInsightSurface): TldInsightAnswer => {
    switch (result.status) {
        case 'public':
            return { kind: 'render', insight: result.insight };
        case 'blocked':
            return { kind: 'redirect', path: surface === 'page' ? TLD_HUB_PATH : TLD_HUB_PAGE_MIRROR_PATH };
        case 'gone':
            return { kind: 'error', status: 410 };
        case 'not_public':
            return { kind: 'error', status: 404 };
        case 'unavailable':
            return { kind: 'error', status: 503, retryAfter: PUBLIC_UNAVAILABLE_RETRY_AFTER_SECONDS };
    }
};
